import i18n from '@/locales/i18n'
import checkpointService from '@/services/checkpoint.service'
import { EStoreModules } from '@/store/storeType'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import { EAlertActions } from '@rfc/rfc-components-library/store'
import {
    EHandler,
    EPaginationOptions,
    type PaginationDataType,
    type PaginationType
} from '@rfc/rfc-components-library/types'
import { getPageSize } from '@rfc/rfc-components-library/utils'
import { ref, type Ref } from 'vue'
import type { Commit, Dispatch } from 'vuex'
import {
    ECheckpointActions,
    ECheckpointMutations,
    type CheckpointState
} from './CheckpointStoreTypes'

const state: Ref<PaginationDataType<CheckpointState>> = ref({
    data: {
        checkpoints: []
    },
    current_page: 1,
    last_page: 0,
    total: 0,
    to: 0,
    from: 0,
    per_page: getPageSize() ?? EPaginationOptions.DEFAULT,

    loading: false
})

const getters = {
    getCheckpoints: () => state.value.data?.checkpoints,
    loading: () => state.value.loading
}

const actions = {
    async [ECheckpointActions.FETCH_CHECKPOINT](
        { commit }: { commit: Commit },
        { eventId, checkpointId }: { eventId: number; checkpointId: number }
    ) {
        commit(ECheckpointMutations.SET_LOADING, true)
        return await checkpointService
            .getSpecificCheckpointFromEvent(eventId, checkpointId)
            .then(res => {
                commit(ECheckpointMutations.SET_CHECKPOINT, res.data)
                return res.data
            })
            .finally(() => commit(ECheckpointMutations.SET_LOADING, false))
    },

    async [ECheckpointActions.FETCH_CHECKPOINTS](
        { commit }: { commit: Commit },
        {
            id,
            params,
            noQueryparams
        }: { id: number; params?: Partial<PaginationType>; noQueryparams?: boolean }
    ) {
        commit(ECheckpointMutations.SET_LOADING, true)
        return await checkpointService
            .getCheckpointsFromEvent(id, params, noQueryparams)
            .then(data => {
                commit(ECheckpointMutations.SET_CHECKPOINTS, data?.data ?? data)
                commit(ECheckpointMutations.SET_TOTAL, data)
                return data?.data ?? data
            })
            .finally(() => {
                commit(ECheckpointMutations.SET_LOADING, false)
            })
    },

    async [ECheckpointActions.CREATE_CHECKPOINT](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { eventId, data }: { eventId: number; data: CheckpointModel }
    ) {
        commit(ECheckpointMutations.SET_LOADING, true)
        return await checkpointService
            .addCheckpointToEvent(eventId, data)
            .then(res => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: ECheckpointActions.CREATE_CHECKPOINT
                })
                return res.data
            })
            .finally(() => {
                commit(ECheckpointMutations.SET_LOADING, false)
            })
    },

    async [ECheckpointActions.UPDATE_CHECKPOINT](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        {
            eventId,
            checkpointId,
            data
        }: { eventId: number; checkpointId: number; data: CheckpointModel }
    ) {
        commit(ECheckpointMutations.SET_LOADING, true)
        return await checkpointService
            .updateCheckpointFromEvent(eventId, checkpointId, data)
            .then(res => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: ECheckpointActions.UPDATE_CHECKPOINT
                })
                return res.data
            })
            .finally(() => {
                commit(ECheckpointMutations.SET_LOADING, false)
            })
    },

    async [ECheckpointActions.DELETE_CHECKPOINT](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { eventId, checkpointId, name }: { eventId: number; checkpointId: number; name: string }
    ) {
        commit(ECheckpointMutations.SET_LOADING, true)
        return await checkpointService
            .deleteCheckpointFromEvent(eventId, checkpointId)
            .then(response => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name,
                    key: ECheckpointActions.DELETE_CHECKPOINT
                })
                return response
            })
            .finally(() => {
                commit(ECheckpointMutations.SET_LOADING, false)
            })
    },

    async [ECheckpointActions.ASSOCIATE_DEVICE](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        {
            device_id,
            checkpoint_id,
            event_id,
            config,
            name,
            force
        }: {
            device_id: number
            checkpoint_id: number
            event_id: number
            config?: Record<string, number>
            force?: boolean
            name: string
        }
    ) {
        commit(ECheckpointMutations.SET_LOADING, true)
        return await checkpointService
            .associateDeviceToCheckpoint(event_id, checkpoint_id, device_id, config, force)
            .then(response => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name,
                    key: ECheckpointActions.ASSOCIATE_DEVICE
                })
                return response
            })
            .finally(() => {
                commit(ECheckpointMutations.SET_LOADING, false)
            })
    },

    async [ECheckpointActions.ERASE_DETECTIONS](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { eventId, checkpointId, name }: { eventId: number; checkpointId: number; name: string }
    ) {
        commit(ECheckpointMutations.SET_LOADING, true)
        return await checkpointService
            .eraseDetectionsFromCheckpoint(eventId, checkpointId)
            .then(response => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name,
                    key: ECheckpointActions.ERASE_DETECTIONS
                })
                return response
            })
            .finally(() => {
                commit(ECheckpointMutations.SET_LOADING, false)
            })
    },

    [EHandler.HANDLE_SUCCESS](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.SUCCESS,
                message: i18n.global.t(`checkpoint.api.success.${key}`, {
                    name
                })
            },
            { root: true }
        )
        return { name }
    },
    [EHandler.HANDLE_ERROR](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.ERROR,
                message: i18n.global.t(`checkpoint.api.error.${key}`, {
                    name
                })
            },
            { root: true }
        )
        return { name }
    }
}

const mutations = {
    [ECheckpointMutations.SET_CHECKPOINTS](_: CheckpointState, checkpoints: CheckpointModel[]) {
        state.value.data!.checkpoints = checkpoints
    },
    [ECheckpointMutations.SET_CHECKPOINT](_: CheckpointState, checkpoint: CheckpointModel) {
        state.value.data!.checkpoint = checkpoint
    },
    [ECheckpointMutations.SET_TOTAL](
        _: CheckpointState,
        data: PaginationDataType<CheckpointModel>
    ) {
        state.value.current_page = data.current_page
        state.value.total = data.total
        state.value.last_page = data.last_page
        state.value.to = data.to
        state.value.from = data.from
    },
    [ECheckpointMutations.SET_LOADING](_: CheckpointState, loading: boolean) {
        state.value.loading = loading
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
